import type { RoundPresentation } from '../types';

export type PresentationKind = RoundPresentation['kind'];

export interface PresentationStyle {
  icon: string;
  label: string;
  preRoundHint: string;
  accentRing: string;
  accentBg: string;
  badgeCls: string;
}

const STYLES: Partial<Record<PresentationKind, PresentationStyle>> = {
  dictation: {
    icon: '🎧',
    label: 'Diktat',
    preRoundHint: 'Lyt — skriv uden at se teksten.',
    accentRing: 'ring-violet-200 dark:ring-violet-500/30',
    accentBg: 'bg-violet-50/80 dark:bg-violet-500/10',
    badgeCls:
      'bg-violet-100 text-violet-800 ring-violet-200 dark:bg-violet-500/15 dark:text-violet-200 dark:ring-violet-500/30',
  },
  echo: {
    icon: '⚡',
    label: 'Flash',
    preRoundHint: 'Se ordet kort — skriv det fra hukommelsen.',
    accentRing: 'ring-amber-200 dark:ring-amber-500/30',
    accentBg: 'bg-amber-50/80 dark:bg-amber-500/10',
    badgeCls:
      'bg-amber-100 text-amber-900 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-200 dark:ring-amber-500/30',
  },
  metronome: {
    icon: '🥁',
    label: 'Rytme',
    preRoundHint: 'Skriv én tast pr. slag — find et jævnt tempo.',
    accentRing: 'ring-emerald-200 dark:ring-emerald-500/30',
    accentBg: 'bg-emerald-50/80 dark:bg-emerald-500/10',
    badgeCls:
      'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30',
  },
  shadow: {
    icon: '📜',
    label: 'Skygge',
    preRoundHint: 'Teksten ruller — følg med uden at tænke.',
    accentRing: 'ring-cyan-200 dark:ring-cyan-500/30',
    accentBg: 'bg-cyan-50/80 dark:bg-cyan-500/10',
    badgeCls:
      'bg-cyan-100 text-cyan-900 ring-cyan-200 dark:bg-cyan-500/15 dark:ring-cyan-500/30',
  },
  story: {
    icon: '📖',
    label: 'Fortælling',
    preRoundHint: 'Lyt til det du hører — skriv prikkerne og dit ord i understregningerne.',
    accentRing: 'ring-rose-200 dark:ring-rose-500/30',
    accentBg: 'bg-rose-50/80 dark:bg-rose-500/10',
    badgeCls:
      'bg-rose-100 text-rose-900 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-500/30',
  },
  story2: {
    icon: '✨',
    label: 'Fortælling 2',
    preRoundHint:
      'Læs og skriv på skærmen — find på ord i huller når historien kalder på det.',
    accentRing: 'ring-fuchsia-200 dark:ring-fuchsia-500/30',
    accentBg: 'bg-fuchsia-50/80 dark:bg-fuchsia-500/10',
    badgeCls:
      'bg-fuchsia-100 text-fuchsia-900 ring-fuchsia-200 dark:bg-fuchsia-500/15 dark:text-fuchsia-200 dark:ring-fuchsia-500/30',
  },
  imagery: {
    icon: '○',
    label: 'Imaginær',
    preRoundHint: 'Ingen tidspres — forestil fingerbevægelsen før du taster.',
    accentRing: 'ring-stone-200 dark:ring-stone-500/30',
    accentBg: 'bg-stone-50/80 dark:bg-stone-500/10',
    badgeCls:
      'bg-stone-100 text-stone-700 ring-stone-200 dark:bg-stone-500/15 dark:text-stone-200 dark:ring-stone-500/30',
  },
};

export function presentationStyle(
  kind: PresentationKind | undefined,
): PresentationStyle | null {
  if (!kind || kind === 'standard') return null;
  return STYLES[kind] ?? null;
}

export function presentationKindFromRound(
  presentation: RoundPresentation | undefined,
): PresentationKind {
  return presentation?.kind ?? 'standard';
}

/** Label for dictation sub-modes (paragraph uses dictation styling). */
export function dictationRoundLabel(
  presentation: RoundPresentation | undefined,
): string {
  if (presentation?.kind !== 'dictation') return 'Diktat';
  if (presentation.dictationMode === 'paragraph') return 'Afsnit-diktat';
  if (presentation.dictationMode === 'unit') return 'Diktat (enhed)';
  return 'Diktat';
}
